/**
 * AS ROTAS DO HUB (14/10/2026, PR-A do plano aprovado pelo Heitor).
 *
 * Hoje a tela é `section` em estado no App: o link do navegador não muda, F5 volta ao Dashboard, Voltar/Avançar não
 * navegam e não dá para mandar o link de uma tela. Este arquivo é o MAPA section ↔ URL, sem efeito na tela ainda: a
 * PR-C liga o App a ele (o section passa a vir do pathname).
 *
 * Regras:
 *  - TODA folha do menu (`config/menu.ts`) tem rota, e só elas: tela nova sem rota reprova no `rotas.test.ts`.
 *  - O caminho de um pai sem tela própria (Financeiro → Notas Fiscais, Licitações, Administrativo…) leva ao 1º filho.
 *  - Caminho desconhecido → dashboard. Nunca tela em branco.
 *  - ⛔ Prefixos PROIBIDOS, que já têm dono: `/p/` e `/proposta/` (proposta pública), `/m/` (o rewrite `/m/:code` do
 *    vercel.json vai ao BACKEND), `/api/` (o BFF de auth). Rota do HUB que começasse com eles nunca chegaria à SPA.
 *  - ⛔ A permissão é decidida por quem chama (`podeVer`), com a mesma árvore filtrada do menu: com URL, digitar
 *    /admin/usuarios renderizaria a tela para quem não tem o módulo; hoje só o menu esconde.
 */
import { MenuItem, menuItems, adminItems, filterMenuByPermission } from './menu';

/** section (id da folha do menu) → caminho. A ordem não importa; a bijeção é testada. */
export const ROTAS: Record<string, string> = {
  dashboard: '/',
  leads: '/crm',
  'nucleo-propostas': '/crm/nucleo-propostas',
  products: '/produtos',

  clients: '/clientes',
  locations: '/clientes/grupos',
  ddrs: '/clientes/ddrs',
  contracts: '/clientes/assinaturas',
  reajustes: '/clientes/reajustes',

  'resumo-financeiro': '/financeiro',
  recebiveis: '/financeiro/recebiveis',
  invoices: '/financeiro/faturas',
  cobranca: '/financeiro/cobranca',
  'fiscal-docs': '/financeiro/notas/documentos',
  'fiscal-certs': '/financeiro/notas/certificados',
  'fiscal-monitor': '/financeiro/notas/monitoramento',
  boletos: '/financeiro/boletos',
  despesas: '/financeiro/despesas',
  'contas-bancarias': '/financeiro/contas',
  'fluxo-caixa': '/financeiro/fluxo-de-caixa',
  emprestimos: '/financeiro/emprestimos',

  'lic-encontrar': '/licitacoes/encontrar',
  'lic-cadastrar': '/licitacoes/cadastrar',
  'lic-disputar': '/licitacoes/disputar',
  'lic-monitorar': '/licitacoes/monitorar',
  'lic-documentos': '/licitacoes/documentos',
  'lic-acessos': '/licitacoes/acessos',
  'comprasnet-infra': '/licitacoes/automacao',

  demandas: '/demandas',

  oficios: '/administrativo/oficios',
  'email-comunicados': '/administrativo/comunicados',
  portabilidade: '/administrativo/portabilidade',
  materials: '/administrativo/materiais',

  'aws-reservations': '/infraestrutura/reservas-aws',
  'ec2-monitoring': '/infraestrutura/monitor-ec2',

  reports: '/admin/relatorios',
  'docs-societarios': '/admin/docs-societarios',
  users: '/admin/usuarios',
  templates: '/admin/email/templates',
  'email-contas': '/admin/email/contas',
  'email-automacoes': '/admin/email/automacoes',
  'email-logs': '/admin/email/logs',
  integrations: '/admin/integracoes',
  settings: '/admin/configuracoes',
};

/** Caminho de PAI sem tela própria → o pai no menu (o destino é o 1º filho dele que a pessoa pode ver). */
export const PAIS: Record<string, string> = {
  '/financeiro/notas': 'fiscal',
  '/licitacoes': 'licitacoes',
  '/administrativo': 'administrativo',
  '/infraestrutura': 'infraestrutura',
};

/**
 * Caminhos que MUDARAM de lugar e continuam valendo (favorito, histórico, link colado num chat). Levam à tela, e o App
 * troca pelo caminho canônico com replace. A1 (26/10/2026): o Email deixou de ser submenu (o "/admin/email" era o pai e
 * abria o 1º filho, Comunicados, que é para onde segue indo), Comunicados foi para o Administrativo e o Núcleo para
 * junto do CRM. A permissão continua valendo: sem ela, cai no dashboard como qualquer caminho.
 */
export const CAMINHOS_ANTIGOS: Record<string, string> = {
  '/admin/email': 'email-comunicados',
  '/admin/email/comunicados': 'email-comunicados',
  '/admin/email/nucleo-propostas': 'nucleo-propostas',
};

export const PREFIXOS_PROIBIDOS = ['/p/', '/proposta/', '/m/', '/api/'];

const ARVORE: MenuItem[] = [...menuItems, ...adminItems];

/** Os ids das FOLHAS (as telas), na ordem do menu. */
export function folhasDoMenu(itens: MenuItem[] = ARVORE): string[] {
  const out: string[] = [];
  for (const i of itens) {
    if (i.children && i.children.length > 0) out.push(...folhasDoMenu(i.children));
    else out.push(i.id);
  }
  return out;
}

function acharPai(id: string, itens: MenuItem[] = ARVORE): MenuItem | null {
  for (const i of itens) {
    if (i.id === id && i.children && i.children.length > 0) return i;
    if (i.children) {
      const achou = acharPai(id, i.children);
      if (achou) return achou;
    }
  }
  return null;
}

/** "/Clientes/DDRs/" → "/clientes/ddrs"; "" → "/". */
export function normalizarCaminho(pathname: string): string {
  const semBarraFinal = String(pathname || '/').trim().replace(/\/+$/, '').toLowerCase();
  return semBarraFinal === '' ? '/' : semBarraFinal;
}

const SECTION_DO_CAMINHO: Record<string, string> = Object.fromEntries(Object.entries(ROTAS).map(([s, c]) => [c, s]));

/** O caminho de uma tela. Section desconhecida → "/" (o dashboard). */
export function rotaDaSection(section: string): string {
  return ROTAS[section] || '/';
}

/**
 * A tela de um caminho. `podeVer` (opcional) recebe o id da folha e diz se a pessoa vê o módulo; sem ele, todas.
 * Desconhecido, ou sem permissão → 'dashboard'. Pai → o 1º filho visível.
 */
export function sectionDaRota(pathname: string, podeVer: (section: string) => boolean = () => true): string {
  const caminho = normalizarCaminho(pathname);
  const direta = SECTION_DO_CAMINHO[caminho] || CAMINHOS_ANTIGOS[caminho];
  if (direta) return podeVer(direta) ? direta : 'dashboard';
  const pai = PAIS[caminho];
  if (pai) {
    const no = acharPai(pai);
    const primeiro = no ? folhasDoMenu(no.children || []).find(podeVer) : undefined;
    return primeiro || 'dashboard';
  }
  return 'dashboard';
}

/**
 * Quais telas a pessoa vê: as folhas da MESMA árvore filtrada do menu (`filterMenuByPermission`, que falha fechado).
 * O dashboard é sempre visível: é o destino de tudo que não se pode ver, e o App sempre abriu nele.
 */
export function podeVerPor(canAccessModule: (module: string) => boolean): (section: string) => boolean {
  const visiveis = new Set(folhasDoMenu(filterMenuByPermission(ARVORE, canAccessModule)));
  return (section: string) => section === 'dashboard' || visiveis.has(section);
}

/**
 * O título da barra do topo, derivado do MENU (15/10/2026): o pai de 1º nível e o rótulo da tela. A barra tinha mapas
 * escritos à mão e, para toda tela fora deles (Licitações inteira, Contas, Despesas…), mostrava o id cru
 * ("lic-monitorar", "contas-bancarias"). `pai` é null para tela de 1º nível; `destinoDoPai` é a 1ª tela do pai que a
 * pessoa vê (a mesma regra da rota de pai). Section que não está no menu → rótulo null.
 */
export function tituloDaSection(
  section: string,
  podeVer: (section: string) => boolean = () => true,
): { pai: string | null; destinoDoPai: string | null; rotulo: string | null } {
  const rotulo = rotuloDaSection(section);
  for (const raiz of ARVORE) {
    if (!raiz.children || raiz.children.length === 0) continue;
    const folhas = folhasDoMenu(raiz.children);
    if (folhas.includes(section)) {
      return { pai: raiz.label, destinoDoPai: folhas.find(podeVer) || null, rotulo };
    }
  }
  return { pai: null, destinoDoPai: null, rotulo };
}

/** O rótulo da tela (para o título da aba do navegador). */
export function rotuloDaSection(section: string, itens: MenuItem[] = ARVORE): string | null {
  for (const i of itens) {
    if (i.id === section && !(i.children && i.children.length > 0)) return i.label;
    if (i.children) {
      const r = rotuloDaSection(section, i.children);
      if (r) return r;
    }
  }
  return null;
}

/**
 * Os DEEP-LINKS ANTIGOS por query, que já estão em e-mails e no Telegram e valem para sempre:
 *  - `?licItem=<id>` → Monitorar com o item aberto; com `&adequar=1` → Cadastrar com o "Adequar ao lance final";
 *  - `?licitacaoItem=<id>`: o APELIDO que o evento da Agenda Google escreve (backend agenda-licitacao.util). O front só
 *    lia `licItem`, e esse link abria o Dashboard;
 *  - `?demanda=DEM-N` → Demandas com a demanda aberta.
 * Devolve null quando a query não tem nenhum. `resto` é a query sem os parâmetros consumidos (o `reset_token` do Login,
 * por exemplo, fica).
 */
export function deepLinkDaQuery(search: string): { section: string; licItem: number | null; adequar: boolean; demanda: string | null; resto: string } | null {
  const p = new URLSearchParams(search || '');
  const bruto = p.get('licItem') ?? p.get('licitacaoItem');
  const id = bruto ? parseInt(bruto, 10) : NaN;
  const licItem = Number.isFinite(id) && id > 0 ? id : null;
  const adequar = licItem != null && p.get('adequar') === '1';
  const demanda = p.get('demanda') || null;
  if (licItem == null && !demanda) return null;
  for (const k of ['licItem', 'licitacaoItem', 'adequar', 'demanda']) p.delete(k);
  const section = demanda ? 'demandas' : adequar ? 'lic-cadastrar' : 'lic-monitorar';
  const resto = p.toString();
  return { section, licItem, adequar, demanda, resto: resto ? `?${resto}` : '' };
}

/**
 * ESTADO NA URL (15/10/2026): o que está aberto na tela vai num parâmetro da query ("?cliente=5"). Só o que se abre;
 * busca e seleção não (decisão do Heitor). Os dois helpers preservam os OUTROS parâmetros.
 */
export function comParametro(pathname: string, search: string, chave: string, valor: string | number): string {
  const q = new URLSearchParams(search);
  q.set(chave, String(valor));
  return `${pathname}?${q.toString()}`;
}

export function semParametro(search: string, chave: string): string {
  const q = new URLSearchParams(search);
  q.delete(chave);
  const r = q.toString();
  return r ? `?${r}` : '';
}

/** O id inteiro positivo do parâmetro, ou null (ausente, vazio, "abc", "0", "-3", "5.2"). */
export function idDoParametro(search: string, chave: string): number | null {
  const v = new URLSearchParams(search).get(chave);
  if (!v || !/^\d+$/.test(v)) return null;
  const n = Number(v);
  return n > 0 ? n : null;
}

/**
 * Troca UM parâmetro da URL atual com REPLACE (estado de tela, não navegação: o histórico não cresce). `null` tira.
 * Preserva os outros parâmetros, o hash e o `history.state` (marcas das cascas, estado do roteador).
 */
export function trocarParametroNaUrl(chave: string, valor: string | number | null): void {
  const q = new URLSearchParams(window.location.search);
  if (valor == null) q.delete(chave); else q.set(chave, String(valor));
  const busca = q.toString() ? `?${q.toString()}` : '';
  if (busca === window.location.search) return;
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${busca}${window.location.hash}`);
}
